import React, { useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

const snoise_glsl = `
// Simplex 3D Noise 
vec4 permute(vec4 x){return mod(((x*34.0)+1.0)*x, 289.0);}
vec4 taylorInvSqrt(vec4 r){return 1.79284291400159 - 0.85373472095314 * r;}
float snoise(vec3 v){ 
  const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;
  const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);
  vec3 i  = floor(v + dot(v, C.yyy) );
  vec3 x0 = v - i + dot(i, C.xxx) ;
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min( g.xyz, l.zxy );
  vec3 i2 = max( g.xyz, l.zxy );
  vec3 x1 = x0 - i1 + 1.0 * C.xxx;
  vec3 x2 = x0 - i2 + 2.0 * C.xxx;
  vec3 x3 = x0 - 1.0 + 3.0 * C.xxx;
  i = mod(i, 289.0 ); 
  vec4 p = permute( permute( permute( 
             i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
           + i.y + vec4(0.0, i1.y, i2.y, 1.0 )) 
           + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));
  float n_ = 1.0/7.0; // N=7
  vec3  ns = n_ * D.wyz - D.xzx;
  vec4 j = p - 49.0 * floor(p * ns.z *ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_ );
  vec4 x = x_ *ns.x + ns.yyyy;
  vec4 y = y_ *ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);
  vec4 b0 = vec4( x.xy, y.xy );
  vec4 b1 = vec4( x.zw, y.zw );
  vec4 s0 = floor(b0)*2.0 + 1.0;
  vec4 s1 = floor(b1)*2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));
  vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
  vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;
  vec3 p0 = vec3(a0.xy,h.x);
  vec3 p1 = vec3(a0.zw,h.y);
  vec3 p2 = vec3(a1.xy,h.z);
  vec3 p3 = vec3(a1.zw,h.w);
  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
  p0 *= norm.x;
  p1 *= norm.y;
  p2 *= norm.z;
  p3 *= norm.w;
  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3) ) );
}
`;

const manifoldVertexShader = `
uniform float uTime;
uniform vec2 uMouse;

varying float vAlpha;
varying vec3 vColor;
varying float vZ;

${snoise_glsl}

void main() {
  vec3 pos = position;
  float t = uTime * 0.1;
  
  // Base manifold geometry evolution
  float noiseBase = snoise(vec3(pos.x * 0.1, pos.y * 0.1, t * 0.5));
  float noiseDetailed = snoise(vec3(pos.x * 0.3, pos.y * 0.3, t));
  
  pos.z += noiseBase * 2.0 + noiseDetailed * 0.5;
  
  // Mouse perturbation (smooth local bending)
  vec3 mouse3D = vec3(uMouse.x * 20.0, uMouse.y * 20.0, 0.0);
  float distToMouse = distance(pos.xy, mouse3D.xy);
  float influence = exp(-distToMouse * 0.08); // Smooth falloff
  
  // Bending towards mouse and adding a ripple
  pos.z += influence * 3.0 * sin(distToMouse * 0.5 - uTime * 2.0);
  
  // Optional symmetry structure emerging
  float symmetryPhase = smoothstep(0.8, 1.0, sin(t * 0.3));
  float angle = atan(pos.y, pos.x);
  float radius = length(pos.xy);
  float symOffset = cos(angle * 4.0) * sin(radius * 0.5);
  pos.z += symOffset * symmetryPhase * 2.0;

  vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
  gl_Position = projectionMatrix * mvPosition;
  
  vZ = pos.z;
  
  // Depth and influence-based alpha
  vAlpha = smoothstep(-10.0, 5.0, mvPosition.z) * 0.15;
  vAlpha += influence * 0.2; // Brighten near mouse
  
  // Soft cyan to muted violet gradient
  vec3 colorCyan = vec3(0.5, 0.9, 1.0);
  vec3 colorViolet = vec3(0.6, 0.4, 0.8);
  vColor = mix(colorCyan, colorViolet, smoothstep(-2.0, 2.0, pos.z));
  vColor = mix(vColor, vec3(1.0), influence * 0.3); // Mix to white near mouse
}
`;

const manifoldFragmentShader = `
varying float vAlpha;
varying vec3 vColor;

void main() {
  gl_FragColor = vec4(vColor, vAlpha);
}
`;

const particleVertexShader = `
uniform float uTime;
uniform vec2 uMouse;

attribute float aRandom;
attribute float aSize;

varying float vAlpha;
varying vec3 vColor;

${snoise_glsl}

vec3 curlNoise(vec3 p) {
    float e = 0.1;
    vec3 dx = vec3(e, 0.0, 0.0);
    vec3 dy = vec3(0.0, e, 0.0);
    vec3 dz = vec3(0.0, 0.0, e);
    
    vec3 p_x0 = vec3(snoise(p - dx), snoise(p - dx + 100.0), snoise(p - dx + 200.0));
    vec3 p_x1 = vec3(snoise(p + dx), snoise(p + dx + 100.0), snoise(p + dx + 200.0));
    vec3 p_y0 = vec3(snoise(p - dy), snoise(p - dy + 100.0), snoise(p - dy + 200.0));
    vec3 p_y1 = vec3(snoise(p + dy), snoise(p + dy + 100.0), snoise(p + dy + 200.0));
    vec3 p_z0 = vec3(snoise(p - dz), snoise(p - dz + 100.0), snoise(p - dz + 200.0));
    vec3 p_z1 = vec3(snoise(p + dz), snoise(p + dz + 100.0), snoise(p + dz + 200.0));
    
    float x = p_y1.z - p_y0.z - p_z1.y + p_z0.y;
    float y = p_z1.x - p_z0.x - p_x1.z + p_x0.z;
    float z = p_x1.y - p_x0.y - p_y1.x + p_y0.x;
    
    return normalize(vec3(x, y, z) / (2.0 * e));
}

void main() {
  vec3 initialPos = position;
  float t = uTime * (0.05 + aRandom * 0.05);
  
  // Drift particles along curl noise (gradient flow illusion)
  // Instead of updating state, we parameterize by time to get continuous paths
  vec3 flowPos = initialPos;
  // We accumulate displacement in a cheap loop or just use a strong displacement field
  vec3 flowDisplacement = curlNoise(initialPos * 0.2 + uTime * 0.02) * 2.0;
  flowDisplacement += curlNoise(initialPos * 0.4 - uTime * 0.05) * 1.0;
  
  vec3 pos = initialPos + flowDisplacement;
  
  // Align with manifold surface roughly
  float noiseBase = snoise(vec3(pos.x * 0.1, pos.y * 0.1, uTime * 0.05));
  pos.z = noiseBase * 2.0;
  
  // Mouse perturbation
  vec3 mouse3D = vec3(uMouse.x * 20.0, uMouse.y * 20.0, 0.0);
  float distToMouse = distance(pos.xy, mouse3D.xy);
  float influence = exp(-distToMouse * 0.1);
  
  // Pull towards mouse slightly and swirl
  vec3 toMouse = mouse3D - pos;
  vec3 swirl = vec3(-toMouse.y, toMouse.x, 0.0) * 0.5;
  pos += mix(vec3(0.0), normalize(toMouse) * 0.5 + swirl, influence);
  pos.z += influence * 3.0 * sin(distToMouse * 0.5 - uTime * 2.0);

  vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
  gl_Position = projectionMatrix * mvPosition;
  
  gl_PointSize = aSize * (20.0 / -mvPosition.z);
  
  vAlpha = (0.2 + 0.3 * aRandom) * smoothstep(25.0, 0.0, length(pos.xy));
  vAlpha += influence * 0.3;
  
  vec3 color1 = vec3(1.0, 1.0, 1.0);
  vec3 color2 = vec3(0.5, 0.9, 1.0);
  vec3 color3 = vec3(0.6, 0.4, 0.8);
  
  float colorMix = aRandom;
  if (colorMix < 0.33) vColor = mix(color1, color2, colorMix * 3.0);
  else if (colorMix < 0.66) vColor = mix(color2, color3, (colorMix - 0.33) * 3.0);
  else vColor = mix(color3, color1, (colorMix - 0.66) * 3.0);
  
  vColor = mix(vColor, vec3(1.0), influence * 0.5);
}
`;

const particleFragmentShader = `
varying float vAlpha;
varying vec3 vColor;

void main() {
  float d = distance(gl_PointCoord, vec2(0.5));
  if (d > 0.5) discard;
  
  // Soft particle edge
  float alpha = (0.5 - d) * 2.0 * vAlpha;
  gl_FragColor = vec4(vColor, alpha);
}
`;

export function MathematicalManifold() {
  const { viewport, mouse } = useThree();
  
  const manifoldMaterial = useRef<THREE.ShaderMaterial>(null);
  const particleMaterial = useRef<THREE.ShaderMaterial>(null);
  
  // Create static grid geometry for the manifold lines
  const gridGeometry = useMemo(() => {
    const geo = new THREE.PlaneGeometry(60, 60, 80, 80);
    return new THREE.WireframeGeometry(geo);
  }, []);
  
  // Create particles
  const [particleGeo, particleCount] = useMemo(() => {
    const count = 15000;
    const geo = new THREE.BufferGeometry();
    const positions = new Float32Array(count * 3);
    const randoms = new Float32Array(count);
    const sizes = new Float32Array(count);
    
    for (let i = 0; i < count; i++) {
      // Distribute randomly in a circle
      const r = 30 * Math.sqrt(Math.random());
      const theta = Math.random() * 2 * Math.PI;
      positions[i * 3] = r * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(theta);
      positions[i * 3 + 2] = (Math.random() - 0.5) * 5;
      
      randoms[i] = Math.random();
      sizes[i] = Math.random() * 1.5 + 0.5;
    }
    
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('aRandom', new THREE.BufferAttribute(randoms, 1));
    geo.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));
    return [geo, count];
  }, []);
  
  // Smooth mouse interpolation to avoid sudden jumps
  const smoothMouse = useRef(new THREE.Vector2(0, 0));

  useFrame((state) => {
    // Lerp mouse for natural feeling perturbations
    smoothMouse.current.lerp(mouse, 0.05);
    
    if (manifoldMaterial.current) {
      manifoldMaterial.current.uniforms.uTime.value = state.clock.elapsedTime;
      manifoldMaterial.current.uniforms.uMouse.value.copy(smoothMouse.current);
    }
    if (particleMaterial.current) {
      particleMaterial.current.uniforms.uTime.value = state.clock.elapsedTime;
      particleMaterial.current.uniforms.uMouse.value.copy(smoothMouse.current);
    }
  });

  return (
    <group>
      {/* The Manifold (Wireframe surface) */}
      <lineSegments geometry={gridGeometry}>
        <shaderMaterial
          ref={manifoldMaterial}
          vertexShader={manifoldVertexShader}
          fragmentShader={manifoldFragmentShader}
          uniforms={{
            uTime: { value: 0 },
            uMouse: { value: new THREE.Vector2() }
          }}
          transparent
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </lineSegments>
      
      {/* The Gradient Flow Particles */}
      <points geometry={particleGeo}>
        <shaderMaterial
          ref={particleMaterial}
          vertexShader={particleVertexShader}
          fragmentShader={particleFragmentShader}
          uniforms={{
            uTime: { value: 0 },
            uMouse: { value: new THREE.Vector2() }
          }}
          transparent
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </points>
    </group>
  );
}
